import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { tenantService } from "../../Services/tenant.service";
import { useTranslation } from "react-i18next";
import ListAdminButtons from "../Common/ListAdminButtons";
import SpinnerLoading from "../Common/SpinnerLoading";
import DeleteModal from "./DeleteModal";
import ResultModal from "../Common/ResultModal";
import Tooltip from "../Common/Tooltip";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";
import { AuthContext } from "../../Contexts/AuthContext";
import { roles } from "../../Config/Roles";

const Index = () => {
  const {
    updateLocName,
    updateLocTenant,
    updateLocStore,
    updateLocPos,
    updateLocTab,
    updateLocKey,
    isMobile,
  } = useContext(GlobalContext);
  const { roleId } = useContext(AuthContext);
  const { master, admin, support } = roles;
  const { t: translator } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [update, setUpdate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  });

  const [search, setSearch] = useState({
    searchText: "",
    isDeactivated: false,
    sortName: "Name",
    isAsc: true,
  });

  const [modal, setModal] = useState({
    title: "",
    text: "",
    show: false,
    btnClass: "",
  });
  const history = useHistory();

  useEffect(() => {
    if (![master, admin, support].includes(Number(roleId)))
      history.push("/error-page/401");

    tenantService
      .get({
        searchText: search.searchText,
        isDeactivated: search.isDeactivated,
        sortName: search.sortName,
        isAsc: search.isAsc,
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      })
      .then((res) => {
        setIsLoading(false);
        setTenants(res.data.tenants);
        setPagination({ ...pagination, totalCount: res.data.totalCount });
      })
      .catch((err) => {
        console.error(err.response);
      });
    updateLocName("tenants");
    updateLocTenant(0);
    updateLocStore(0);
    updateLocPos(0);
    updateLocTab(0);
    updateLocKey(0);
  }, [update, search]);

  const handleToUpdate = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/update-tenant/${selectedItems}`);
  };

  const handleToDetail = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/tenants/${selectedItems}`);
  };

  const handleClose = () => {
    setModal({ show: false });
    history.push("/tenants");
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const confirmDelete = () => {
    let deletePromises = [];

    selectedItems.forEach((x) => {
      deletePromises.push(tenantService.deleteTenant(x));
    });

    Promise.all(deletePromises)
      .then((data) => {
        setShowDeleteModal(false);
        setModal({
          ...modal,
          title: "Tenant-ResultModal-DeleteSuccess-Title",
          text: "Tenant-ResultModal-DeleteSuccess-Text",
          show: true,
          btnClass: "btn btn-primary",
        });
        setUpdate(new Date());
      })
      .catch((err) =>
        setModal({
          ...modal,
          title: "Tenant-ResultModal-DeleteError-Title",
          text: "Tenant-ResultModal-DeleteError-Text",
          show: true,
          btnClass: "btn btn-primary",
        })
      );
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems([...selectedItems, row.tenantId]);
    } else {
      setSelectedItems(selectedItems.filter((p) => p !== row.tenantId));
    }
  };

  const handleOnSelectAll = (isSelect) => {
    if (isSelect) {
      setSelectedItems(tenants.map((x) => x.tenantId));
    } else {
      setSelectedItems([]);
    }
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setPagination({ ...pagination, pageIndex: 0 });
    setUpdate(new Date());
  };

  const handleTableChange = (type, { page, sizePerPage }) => {
    setIsLoading(true);
    setPagination({
      ...pagination,
      pageIndex: sizePerPage != pagination.pageSize ? 0 : page - 1,
      pageSize: sizePerPage,
    });
    setUpdate(new Date());
  };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      if (e.target.cellIndex) {
        history.push(`/tenants/${row.tenantId}`);
      }
    },
  };

  const RemoteAll = ({ data, page, sizePerPage, totalSize, onTableChange }) => (
    <div>
      <BootstrapTable
        remote
        keyField="tenantId"
        data={data}
        columns={columns}
        rowClasses="boostrap-table-row"
        noDataIndication={translator("No-Data")}
        rowEvents={rowEvents}
        pagination={paginationFactory({
          page,
          sizePerPage,
          totalSize,
        })}
        onTableChange={onTableChange}
      />
    </div>
  );

  const sorting = (sortName) => {
    setSearch({ ...search, sortName, isAsc: !search.isAsc });
  };

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            checked={selectedItems.find((x) => x === row.tenantId)}
            onChange={(e) => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function actionHeaderFormatter(cell, rows) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            checked={
              tenants &&
              tenants.length > 0 &&
              !tenants
                .map((p) => p.tenantId)
                .some((p) => selectedItems.indexOf(p) < 0)
            }
            onChange={(e) => handleOnSelectAll(e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }
  function streetFormatter(cell, row) {
    return `${cell} ${row.streetNumber}${row.streetNumberExtension}`;
  }
  const columns = [
    {
      dataField: "tenantId",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "50px", textAlign: "center" };
      },
      headerFormatter: actionHeaderFormatter,
      formatter: actionFormatter,
    },
    {
      dataField: "name",
      text: translator("Tentats-TableHeader-Name"),
      headerEvents: { onClick: () => sorting("Name") },
      sort: true,
      sortCaret: (order, column) => {
        if (search.sortName !== "Name")
          return <i className="fa fa-fw fa-sort"></i>;
        if (search.sortName === "Name") {
          return search.isAsc !== true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
    },
    {
      dataField: "taxNumber",
      headerStyle: (colum, colIndex) => {
        return { width: "120px", textAlign: "left" };
      },
      text: translator("Tenants-TableHeader-Oib"),
      headerEvents: { onClick: () => sorting("TaxNumber") },
      sort: true,
      sortCaret: (order, column) => {
        if (search.sortName !== "TaxNumber")
          return <i className="fa fa-fw fa-sort"></i>;
        if (search.sortName === "TaxNumber") {
          return search.isAsc !== true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "code",
      headerStyle: (colum, colIndex) => {
        return { width: "120px", textAlign: "left" };
      },
      text: translator("Tenants-TableHeader-Code"),
      headerEvents: { onClick: () => sorting("Code") },
      sort: true,
      sortCaret: (order, column) => {
        if (search.sortName !== "Code")
          return <i className="fa fa-fw fa-sort"></i>;
        if (search.sortName === "Code") {
          return search.isAsc !== true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "owner",
      text: translator("Tenants-TableHeader-Owner"),
      headerEvents: { onClick: () => sorting("Owner") },
      sort: true,
      sortCaret: (order, column) => {
        if (search.sortName !== "Owner")
          return <i className="fa fa-fw fa-sort"></i>;
        if (search.sortName === "Owner") {
          return search.isAsc !== true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
    {
      dataField: "street",
      text: translator("Tenants-TableHeader-Address"),
      headerEvents: { onClick: () => sorting("Street") },
      sort: true,
      formatter: streetFormatter,
      sortCaret: (order, column) => {
        if (search.sortName !== "Street")
          return <i className="fa fa-fw fa-sort"></i>;
        if (search.sortName === "Street") {
          return search.isAsc !== true ? (
            <i className="fa fa-fw fa-sort-up"></i>
          ) : (
            <i className="fa fa-fw fa-sort-down"></i>
          );
        }
      },
      hidden: Boolean(isMobile),
    },
  ];

  return (
    <div className="p-2 pt-3">
      <div className="card container-fluid p-0">
        <div className="card-header card-wrap">
          <span>{translator("Tenats-List-Title")}</span>{" "}
          {!isLoading ? (
            <span className="badge badge-pill badge-primary">
              {" "}
              {pagination.totalCount}
            </span>
          ) : null}
        </div>

        <div className="card-body">
          <fieldset className="scheduler-border">
            <legend className="scheduler-border">
              {translator("Tenants-List-Filters")}
            </legend>
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="form-group col-lg-3 col-md-4 col-xs-12">
                <label>{translator("Tenants-Filters-Name")}</label>{" "}
                <Tooltip text={translator("TenantTooltip-Msg")} />
                <input
                  type="text"
                  className="form-control"
                  value={search.searchText}
                  onChange={(event) => {
                    setIsLoading(true);
                    setSearch({ ...search, searchText: event.target.value });
                    setPagination({ ...pagination, pageIndex: 0 });
                  }}
                />
              </div>
              <div className="form-group col-lg-2 col-md-4 col-xs-12">
                <label className="labelCont mt-5">
                  <input
                    type="checkbox"
                    name="cashDefault"
                    checked={search.isDeactivated}
                    onChange={(event) => {
                      setIsLoading(true);
                      setSearch({
                        ...search,
                        isDeactivated: event.target.checked,
                      });
                      setPagination({ ...pagination, pageIndex: 0 });
                    }}
                  />
                  <span className="checkmark"></span>
                </label>
                <span>{translator("POS-ShowDeactivated")}</span>
              </div>
            </form>
          </fieldset>
          <ListAdminButtons
            selectedItemsLength={selectedItems.length}
            handleToDetail={handleToDetail}
            handleToUpdate={handleToUpdate}
            handleDelete={() => setShowDeleteModal(true)}
            handleRefresh={handleRefresh}
          />

          {isLoading && (
            <div className="dashboard-card-spinner">
              <SpinnerLoading />
            </div>
          )}

          {!isLoading && (
            <RemoteAll
              data={tenants}
              page={pagination.pageIndex + 1}
              sizePerPage={pagination.pageSize}
              totalSize={pagination.totalCount}
              onTableChange={handleTableChange}
            />
          )}
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("Tenant-DeleteModal-Title")}
        text={translator("Tenant-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
      <ResultModal
        show={modal.show}
        title={modal.title}
        text={modal.text}
        btnClass={modal.btnClass}
        handleClose={handleClose}
      />
    </div>
  );
};

export default Index;
